/**
 * Adding new SVGs (e.g. from Figma):
 * Run from frontend/:
 *   npx svgo --config=./svgo.config.cjs -f ./components/Icon/menu-svg -r
 *   npx svgo --config=./svgo.config.cjs -f ./components/Icon/inline-svg -r
 *
 * This strips no-op <mask> elements that Figma exports.
 * They're invisible in Chrome but clip icons in Firefox.
 * The webpack build also runs this via svgo.config.cjs, but turbopack
 * (dev mode) can't load the custom plugin, so always run it on source files.
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Image from '../Image';
import s from './Icon.module.scss';

//Menu icons desktop
import SvgHome from './menu-svg/home.svg';
import SvgToolbox from './menu-svg/toolbox.svg';
import SvgNews from './menu-svg/mediamonitor.svg';
import SvgReports from './menu-svg/reports.svg';
import SvgConnections from './menu-svg/connections.svg';
import SvgInfoRequests from './menu-svg/info-requests.svg';
import SvgWatchtower from './menu-svg/watchtower.svg';
import SvgFlag from './inline-svg/flag.svg';

//Menu icons mobile
import SvgHomeMobile from './menu-svg/fill--home.svg';
import SvgToolboxMobile from './menu-svg/fill--toolbox.svg';
import SvgNewsMobile from './menu-svg/fill--mediamonitor.svg';
import SvgReportsMobile from './menu-svg/fill--reports.svg';
import SvgConnectionsMobile from './menu-svg/fill--connections.svg';
import SvgInfoRequestsMobile from './menu-svg/fill--info-requests.svg';
import SvgWatchtowerMobile from './menu-svg/fill--watchtower.svg';

import Arrow from './img/arrow.svg';
import Bullet from './img/bullet.svg';
import Chevron from './img/chevron.svg';
import Download from './img/download.svg';
import External from './img/external.svg';

import Search from './inline-svg/search.svg';
import SvgShare from './inline-svg/share.svg';
import SvgArrow from './inline-svg/arrow.svg';
import SvgArrowRight from './inline-svg/arrow-right.svg';
import SvgArrowRightBlack from './inline-svg/arrow-right--black.svg';
import SvgArrowLeftBlack from './inline-svg/arrow-left--black.svg';
import SvgClose from './inline-svg/close.svg';
import SvgCloseRound from './inline-svg/close--round.svg';
import SvgCloseSmall from './inline-svg/close-small.svg';
import SvgCloseRed from './inline-svg/close--red.svg';
import SvgSelectChevron from './inline-svg/select-chevron.svg';
import SvgSelectChevronWhite from './inline-svg/select-chevron-white.svg';
import SvgArrowDown from './inline-svg/arrow-down.svg';
import SvgDownload from './inline-svg/download.svg';
import SvgExternal from './inline-svg/external.svg';
import SvgFacebook from './inline-svg/facebook.svg';
import SvgHamburger from './inline-svg/hamburger.svg';
import SvgInstagram from './inline-svg/instagram.svg';
import SvgLinkedIn from './inline-svg/linkedin.svg';
import SvgMail from './inline-svg/mail.svg';
import SvgPhone from './inline-svg/phone.svg';
import SvgPin from './inline-svg/pin.svg';
import SvgKeep from './inline-svg/keep.svg';
import SvgKeepOff from './inline-svg/keep-off.svg';
import SvgArrowRightSmall from './inline-svg/arrow-right-small.svg';
import SvgExpand from './inline-svg/expand.svg';
import SvgOpenMenu from './inline-svg/open_menu.svg';
import SvgUserProfile from './inline-svg/user-profile.svg';
import SvgUserProfileThick from './inline-svg/user-profile-thick.svg';
import SvgMoreVertical from './inline-svg/more-vert.svg';
import SvgLogout from './inline-svg/logout.svg';
import SvgLogo from './inline-svg/logo.svg';
import SvgLogoWhite from './inline-svg/logo-white.svg';
import SvgTbox from './inline-svg/tbox.svg';
import SvgLogoName from './inline-svg/logo-name.svg';
import SvgLogoOnlyName from './inline-svg/logo-only-text.svg';
import SvgLogoLarge from './inline-svg/logo-large-white.svg';
import SvgHomework from './inline-svg/homework.svg';
import SvgHammer from './inline-svg/hammer.svg';
import SvgFactory from './inline-svg/factory.svg';
import SvgFactoryOutline from './inline-svg/factory--outline.svg';
import SvgApartment from './inline-svg/apartment.svg';
import SvgSupport from './inline-svg/support.svg';
import SvgCheckSmall from './inline-svg/check-small.svg';
import SvgCheckSmallWhite from './inline-svg/check-small--white.svg';
import SvgCheckSmallBlackBackground from './inline-svg/check-small--black-background.svg';
import SvgCheckGreen from './inline-svg/check--green.svg';
import SvgCheckGreenCirle from './inline-svg/check-circle--green.svg';
import SvgCheckGreenCirleFilled from './inline-svg/check-circle-filled--green.svg';
import SvgSupportCircle from './inline-svg/support-circle.svg';
import SvgAccountBalance from './inline-svg/account-balance.svg';
import SvgAccountCircle from './inline-svg/account-circle.svg';
import SvgAgriculture from './inline-svg/agriculture.svg';
import SvgAvTimer from './inline-svg/av-timer.svg';
import SvgCaptivePortal from './inline-svg/captive-portal.svg';
import SvgConcierge from './inline-svg/concierge.svg';
import SvgGavel from './inline-svg/gavel.svg';
import SvgError from './inline-svg/error.svg';
import SvgHowToVote from './inline-svg/how-to-vote.svg';
import SvgMilitaryTech from './inline-svg/military-tech.svg';
import SvgMonitoring from './inline-svg/monitoring.svg';
import SvgQuickReferenceAll from './inline-svg/quick-reference-all.svg';
import SvgQuiz from './inline-svg/quiz.svg';
import SvgThingsToDo from './inline-svg/things-to-do.svg';
import SvgFilter from './inline-svg/filter.svg';
import SvgSearchOff from './inline-svg/search-off.svg';
import SvgFilterWhite from './inline-svg/filter-white.svg';
import SvgTimer from './inline-svg/timer.svg';
import SvgTower from './inline-svg/tower.svg';
import SvgNotFound from './inline-svg/not-found.svg';
import SvgReportsSearch from './inline-svg/reports-search.svg';
import SvgFileSave from './inline-svg/file-save.svg';
import SvgDocumentDownload from './inline-svg/document-download.svg';
import SvgArrowExternalPink from './inline-svg/arrow-external--pink.svg';
import SvgArrowExternalBlue from './inline-svg/arrow-external--blue.svg';
import SvgPerson from './inline-svg/person.svg';
import SvgToc from './inline-svg/toc.svg';
import SvgCampaign from './inline-svg/campaign.svg';
import SvgUpload from './inline-svg/upload.svg';
import SvgFolderUpload from './inline-svg/folder-upload.svg';
import SvgTrash from './inline-svg/trash.svg';
import SvgFile from './inline-svg/file.svg';
import SvgTop10 from './inline-svg/top10.svg';
import SvgHomeWork from './inline-svg/home-work.svg';
import SvgSuitcase from './inline-svg/suitcase.svg';
import SvgTop10New from './inline-svg/top-10.svg';
import SvgBookmark from './inline-svg/bookmark.svg';
import SvgBookmarkRemove from './inline-svg/bookmark-remove.svg';
import SvgBookmarkWhite from './inline-svg/bookmark-white.svg';
import SvgBookmarkEmpty from './inline-svg/bookmark-empty.svg';
import SvgBookmarkRemoving from './inline-svg/bookmark-removing.svg';
import SvgBookmarkCheck from './inline-svg/bookmark-check.svg';
import SvgGroupPeople from './inline-svg/group-people.svg';
import SvgWarning from './inline-svg/warning.svg';
import SvgSyncError from './inline-svg/sync-error.svg';
import SvgFlowmap from './inline-svg/flowmap.svg';
import SvgFlowmapBlue from './inline-svg/flowmap--blue.svg';
import SvgStacks from './inline-svg/stacks.svg';
import SvgPanelCloseBlack from './inline-svg/panel-close-black.svg';
import SvgPanelOpenWhite from './inline-svg/panel-open-white.svg';
import SvgSaveFilters from './inline-svg/save-filters.svg';
import SvgEdit from './inline-svg/icon-edit.svg';
import SvgDelete from './inline-svg/icon-delete.svg';
import SvgCopy from './inline-svg/icon-copy.svg';
import SvgComment from './inline-svg/comment.svg';
import SvgAddComment from './inline-svg/add-comment.svg';
import SvgReportsDashboard from './inline-svg/reports-dashboard.svg';
import SvgMediaMonitorDashboard from './inline-svg/mediamonitor-dashboard.svg';
import SvgRocket from './inline-svg/rocket.svg';
import SvgRocketFire from './inline-svg/rocket-fire.svg';

const capitalizeFirstLetter = (string) =>
    string.charAt(0).toUpperCase() + string.slice(1);

const Icon = ({
    name = '',
    className = '',
    asImg = false,
    asBg = false,
    ...rest
}) => {
    const icons = {
        Logo: SvgLogo,
        LogoWhite: SvgLogoWhite,
        Tbox: SvgTbox,
        LogoName: SvgLogoName,
        LogoOnlyName: SvgLogoOnlyName,
        Arrow: asBg ? Arrow : SvgArrow,
        ArrowRight: SvgArrowRight,
        ArrowRightBlack: SvgArrowRightBlack,
        ArrowLeftBlack: SvgArrowLeftBlack,
        Bullet: asBg ? Bullet : null,
        Chevron: asBg ? Chevron : null,
        Close: SvgClose,
        CloseRound: SvgCloseRound,
        CloseSmall: SvgCloseSmall,
        CloseRed: SvgCloseRed,
        Download: asBg ? Download : SvgDownload,
        External: asBg ? External : SvgExternal,
        Facebook: SvgFacebook,
        Flowmap: SvgFlowmap,
        FlowmapBlue: SvgFlowmapBlue,
        Stacks: SvgStacks,
        Hamburger: SvgHamburger,
        Instagram: SvgInstagram,
        LinkedIn: SvgLinkedIn,
        Mail: SvgMail,
        Phone: SvgPhone,
        Pin: SvgPin,
        Keep: SvgKeep,
        KeepOff: SvgKeepOff,
        ArrowRightSmall: SvgArrowRightSmall,
        UserProfile: SvgUserProfile,
        UserProfileThick: SvgUserProfileThick,
        MoreVertical: SvgMoreVertical,
        Logout: SvgLogout,
        Toolbox: SvgToolbox,
        News: SvgNews,
        Reports: SvgReports,
        Home: SvgHome,
        Homework: SvgHomework,
        Hammer: SvgHammer,
        Search: Search,
        Share: SvgShare,
        Flag: SvgFlag,
        Factory: SvgFactory,
        FactoryOutline: SvgFactoryOutline,
        Apartment: SvgApartment,
        Support: SvgSupport,
        CheckSmall: SvgCheckSmall,
        CheckSmallWhite: SvgCheckSmallWhite,
        CheckSmallBlackBackground: SvgCheckSmallBlackBackground,
        CheckGreen: SvgCheckGreen,
        CheckGreenCircle: SvgCheckGreenCirle,
        CheckGreenCircleFilled: SvgCheckGreenCirleFilled,
        SupportCircle: SvgSupportCircle,
        AccountBalance: SvgAccountBalance,
        Agriculture: SvgAgriculture,
        AvTimer: SvgAvTimer,
        CaptivePortal: SvgCaptivePortal,
        Concierge: SvgConcierge,
        Gavel: SvgGavel,
        Error: SvgError,
        HowToVote: SvgHowToVote,
        MilitaryTech: SvgMilitaryTech,
        Monitoring: SvgMonitoring,
        QuickReferenceAll: SvgQuickReferenceAll,
        Quiz: SvgQuiz,
        ThingsToDo: SvgThingsToDo,
        Filter: SvgFilter,
        SearchOff: SvgSearchOff,
        FilterWhite: SvgFilterWhite,
        Timer: SvgTimer,
        SelectChevron: SvgSelectChevron,
        SelectChevronWhite: SvgSelectChevronWhite,
        Tower: SvgTower,
        NotFound: SvgNotFound,
        ArrowDown: SvgArrowDown,
        Connections: SvgConnections,
        InfoRequests: SvgInfoRequests,
        Watchtower: SvgWatchtower,
        HomeMobile: SvgHomeMobile,
        ToolboxMobile: SvgToolboxMobile,
        NewsMobile: SvgNewsMobile,
        ReportsMobile: SvgReportsMobile,
        ConnectionsMobile: SvgConnectionsMobile,
        InfoRequestsMobile: SvgInfoRequestsMobile,
        WatchtowerMobile: SvgWatchtowerMobile,
        ReportsSearch: SvgReportsSearch,
        FileSave: SvgFileSave,
        DocumentDownload: SvgDocumentDownload,
        ArrowExternalPink: SvgArrowExternalPink,
        ArrowExternalBlue: SvgArrowExternalBlue,
        AccountCircle: SvgAccountCircle,
        Person: SvgPerson,
        Toc: SvgToc,
        Campaign: SvgCampaign,
        Upload: SvgUpload,
        FolderUpload: SvgFolderUpload,
        Trash: SvgTrash,
        File: SvgFile,
        Top10: SvgTop10,
        HomeWork: SvgHomeWork,
        Suitcase: SvgSuitcase,
        Top10New: SvgTop10New,
        Expand: SvgExpand,
        OpenMenu: SvgOpenMenu,
        LogoLarge: SvgLogoLarge,
        Bookmark: SvgBookmark,
        BookmarkMinus: SvgBookmarkRemove,
        BookmarkWhite: SvgBookmarkWhite,
        BookmarkWhiteMobile: SvgBookmarkWhite,
        BookmarkEmpty: SvgBookmarkEmpty,
        BookmarkRemoving: SvgBookmarkRemoving,
        BookmarkCheck: SvgBookmarkCheck,
        GroupPeople: SvgGroupPeople,
        Warning: SvgWarning,
        SyncError: SvgSyncError,
        LogoLarge: SvgLogoLarge,
        LogoLarge: SvgLogoLarge,
        PanelCloseBlack: SvgPanelCloseBlack,
        PanelOpenWhite: SvgPanelOpenWhite,
        SaveFilters: SvgSaveFilters,
        Edit: SvgEdit,
        Delete: SvgDelete,
        Copy: SvgCopy,
        Comment: SvgComment,
        AddComment: SvgAddComment,
        ReportsDashboard: SvgReportsDashboard,
        MediaMonitorDashboard: SvgMediaMonitorDashboard,
        Rocket: SvgRocket,
        RocketFire: SvgRocketFire,
    };
    const Comp = icons[capitalizeFirstLetter(name)];
    const type = asImg ? 'Img' : asBg ? 'Bg' : 'Svg';
    if (!Comp) {
        console.warn(`Icon ${type} "${name}" is missing.`);
        return null;
    }

    const classes = classNames(s.Root, [s[`Root--${type}`]], className);
    const style = { maskImage: `url('${Comp.src}')` };
    if (asImg) {
        return <Image {...Comp} {...rest} className={classes} />;
    } else if (asBg) {
        return <i {...rest} style={style} className={classes} />;
    } else {
        return <Comp {...rest} className={classes} />;
    }
};

Icon.propTypes = {
    name: PropTypes.string.isRequired,
    className: PropTypes.string,
    asImg: PropTypes.bool,
};

export default Icon;
